import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { guardarCertificadoAsistenciaService } from "../../../Service/Service.certificados";
import { createPDFpermisosAsistencia } from "../../../Service/Service.certificadosPDF";
import { CITA_FINALIZADA_SENTINEL } from "../../../utils/citaFinalizadaAlert";
import { GetTokenDecoded } from "../../../utils/storeUsuario";
import { hcError, hcExito } from "../hcSwal";
import HCModal from "../HCModal";
import { DateBadge, Field, OrderBadge, PrintPreview, Section } from "../HCPiezas";
import TabDiagnosticos from "./TabDiagnosticos";

/* ============================================================================
   Certificado de asistencia a consulta — migrado a HCModal.

   Payload y endpoint idénticos: POST /certificado_asistencia_orden.
   `area_atencion` se queda como texto libre: es una columna de prosa
   (`asistencia_orden.area_atencion`), no hay catálogo y el cliente pidió
   dejarlo como estaba.

   Lleva incorporado el arreglo del número: el controller compone
   `${numero_orden}-${correlativo}` y NO lee `numero_asistencia`, que era el
   nombre que mandaba el modal viejo. Por eso todos los certificados de
   asistencia se guardaban como "-001", sin prefijo.
   ========================================================================== */

const FORM_VACIO = {
  idcita: 0,
  IdCategoriaCertificado: 0,
  IdCategoriaExamen: 0,
  idpaciente: 0,
  idclinica: 0,
  fecha: moment().format("YYYY-MM-DD"),
  numero_orden: "",
  hora_desde: "",
  hora_hasta: "",
  area_atencion: "",
};

/** Duración en minutos entre dos "HH:mm". Null si no se puede calcular. */
function duracion(desde, hasta) {
  if (!desde || !hasta) return null;
  const m = moment(hasta, "HH:mm").diff(moment(desde, "HH:mm"), "minutes");
  return Number.isNaN(m) ? null : m;
}

function formatoDuracion(min) {
  if (min === null) return "—";
  if (min < 0) return "revisar";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const r = min % 60;
  return r === 0 ? `${h} h` : `${h} h ${r} min`;
}

export default function ModalAsistenciaHC({
  show,
  onHide,
  onSaveSuccess,
  cita,
  paciente,
  IdCategoriaExamen,
  ListaCitaPaciente,
}) {
  const userLocal = GetTokenDecoded();
  const [tab, setTab] = useState("asistencia");
  const [form, setForm] = useState(FORM_VACIO);
  const [loading, setLoading] = useState(false);
  const [intentado, setIntentado] = useState(false);
  const horaRef = useRef(null);

  useEffect(() => {
    if (!show || !cita) return;
    setForm({
      ...FORM_VACIO,
      idcita: cita.idcita,
      idpaciente: cita.pacientes?.idpaciente,
      idclinica: userLocal?.idclinica ?? 0,
      IdCategoriaExamen,
      // Con el nombre que el backend SÍ lee (antes iba como `numero_asistencia`).
      numero_orden: `ASIST-${cita.idcita}-CECOLON`,
      fecha: moment().format("YYYY-MM-DD"),
      // La cita ya sabe a qué hora empezó: se propone y se puede corregir.
      hora_desde: cita.hora_inicio ? String(cita.hora_inicio).slice(0, 5) : "",
      hora_hasta: cita.hora_fin ? String(cita.hora_fin).slice(0, 5) : "",
    });
    setIntentado(false);
    setTab("asistencia");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, cita, IdCategoriaExamen]);

  const mins = duracion(form.hora_desde, form.hora_hasta);

  const faltantes = useMemo(() => {
    const f = [];
    if (!form.hora_desde) f.push("Hora de entrada");
    if (!form.hora_hasta) f.push("Hora de salida");
    if (mins !== null && mins < 0) f.push("La salida es anterior a la entrada");
    return f;
  }, [form.hora_desde, form.hora_hasta, mins]);

  const guardar = async () => {
    setIntentado(true);
    if (faltantes.length > 0) {
      horaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setLoading(true);
    const data = await guardarCertificadoAsistenciaService(form);
    if (data === CITA_FINALIZADA_SENTINEL) {
      setLoading(false);
      return;
    }
    if (!data || data.success === false) {
      hcError("No se pudo guardar", "Intenta de nuevo en unos segundos.");
      setLoading(false);
      return;
    }
    hcExito("Certificado de asistencia emitido", formatoDuracion(mins));
    setLoading(false);
    const id = data?.data?.idasistencia_orden;
    if (id) await createPDFpermisosAsistencia(id);
    if (onSaveSuccess) await onSaveSuccess();
    cerrar();
  };

  const cerrar = () => {
    setForm(FORM_VACIO);
    onHide && onHide();
  };

  const diagnosticos = cita?.diagnostico_cita_paciente || [];
  const nombre = paciente?.nombres_completos || cita?.pacientes?.nombres_completos || "el paciente";
  const subtitulo = [
    nombre,
    paciente?.edad ? `${paciente.edad} a` : null,
    paciente?.cedula ? `CI ${paciente.cedula}` : null,
    cita?.idcita ? `Cita ${cita.idcita}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <HCModal
      isOpen={!!show}
      size="md"
      title="Certificado de asistencia a consulta"
      subtitle={subtitulo}
      meta={[
        <DateBadge key="f" value={moment(form.fecha).format("DD/MM/YYYY")} label="Fecha de emisión" />,
        <OrderBadge key="o" code={form.numero_orden} label="N.º de certificado" />,
      ]}
      tabs={[
        { key: "asistencia", label: "Asistencia" },
        { key: "diagnostico", label: "Diagnóstico", count: diagnosticos.length },
      ]}
      activeTab={tab}
      onTabChange={setTab}
      validation={{ missing: faltantes }}
      primaryLabel="Emitir e imprimir"
      onPrimary={guardar}
      primaryLoading={loading}
      onClose={cerrar}
      onCancel={cerrar}
      hayCambiosSinGuardar={Boolean(form.area_atencion || form.hora_desde || form.hora_hasta)}
    >
      {tab === "asistencia" ? (
        <Section title="Datos de la atención">
          <Field
            label="Área de atención"
            htmlFor="hc-as-area"
            hint="Texto libre (no hay catálogo en el sistema)"
          >
            <input
              id="hc-as-area"
              type="text"
              placeholder="Medicina general, obstetricia…"
              value={form.area_atencion}
              onChange={(e) => setForm({ ...form, area_atencion: e.target.value })}
            />
          </Field>

          <div className="hcgrid hcgrid--3" ref={horaRef}>
            <Field
              label="Hora de entrada"
              required
              htmlFor="hc-as-desde"
              hint="Propuesta desde la hora de la cita"
              error={intentado && !form.hora_desde ? "Obligatorio" : null}
            >
              <input
                id="hc-as-desde"
                type="time"
                value={form.hora_desde}
                aria-invalid={intentado && !form.hora_desde ? "true" : undefined}
                onChange={(e) => setForm({ ...form, hora_desde: e.target.value })}
              />
            </Field>
            <Field
              label="Hora de salida"
              required
              htmlFor="hc-as-hasta"
              error={
                intentado && !form.hora_hasta
                  ? "Obligatorio"
                  : mins !== null && mins < 0
                  ? "Es anterior a la entrada"
                  : null
              }
            >
              <input
                id="hc-as-hasta"
                type="time"
                value={form.hora_hasta}
                aria-invalid={(intentado && !form.hora_hasta) || (mins !== null && mins < 0) ? "true" : undefined}
                onChange={(e) => setForm({ ...form, hora_hasta: e.target.value })}
              />
            </Field>
            <Field label="Duración" hint="Calculada">
              <div className="hcdias" aria-live="polite">
                {formatoDuracion(mins)}
              </div>
            </Field>
          </div>

          <PrintPreview>
            Se certifica que <b>{nombre}</b> asistió a consulta el{" "}
            {moment(form.fecha).format("DD/MM/YYYY")}
            {form.hora_desde && form.hora_hasta ? `, de ${form.hora_desde} a ${form.hora_hasta}` : ""}
            {form.area_atencion ? `, en el área de ${form.area_atencion}` : ""}.
          </PrintPreview>
        </Section>
      ) : (
        <TabDiagnosticos diagnosticos={diagnosticos} onRefrescar={ListaCitaPaciente} />
      )}
    </HCModal>
  );
}
